import React, { useContext, useCallback, useState, useRef, useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import 'chartjs-adapter-moment';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from "chart.js";
import AuthContext from "../../../contexts/auth-context";

import { ChartHeader, ResolutionToggle, applyChartTheme, CHART_THEME } from "../../Concession/charts/chartTheme";
ChartJS.register(annotationPlugin);

// =============================================================================
// DUMMY DATA GENERATOR - Remove this section when using real data
// =============================================================================
const generateDummyFloorData = (floors, startMinute = 600, endMinute = 1440) => {
  const dummyData = [];
  const baseDate = new Date();
  baseDate.setHours(0, 0, 0, 0);

  for (let minute = startMinute; minute <= endMinute; minute++) {
    const time2 = new Date(baseDate.getTime() + minute * 60000).toISOString();
    
    // Generate traffic for each floor's zones
    const traffic = [];
    let total = 0;

    floors.forEach(floor => {
      // Simulate occupancy pattern: ramps up, peaks around game time, tapers off
      const gameTimePeak = 780; // ~1 PM peak
      const distanceFromPeak = Math.abs(minute - gameTimePeak);
      const peakFactor = Math.max(0, 1 - distanceFromPeak / 300);
      
      // Add some randomness and floor-specific patterns
      const floorMultiplier = floor.id === 1 ? 1.2 : 0.8; // Floor 1 busier
      const baseOccupancy = floor.maxOccupancy * 0.3; // 30% base
      const variableOccupancy = floor.maxOccupancy * 0.6 * peakFactor * floorMultiplier;
      const noise = (Math.random() - 0.5) * floor.maxOccupancy * 0.15;
      
      let floorOccupancy = Math.round(baseOccupancy + variableOccupancy + noise);
      
      // Occasionally simulate breaches (10% chance during peak hours)
      if (minute > 720 && minute < 900 && Math.random() < 0.1) {
        floorOccupancy = Math.round(floor.maxOccupancy * (1.05 + Math.random() * 0.15));
      }
      
      floorOccupancy = Math.max(0, floorOccupancy);
      
      // Distribute across zones on this floor
      floor.zoneSensorIds.forEach(zoneId => {
        const zoneTraffic = Math.round(floorOccupancy / floor.zoneSensorIds.length);
        traffic.push({ id: zoneId, traffic: zoneTraffic });
        total += zoneTraffic;
      });
    });

    const maxOccupancy = floors.reduce((sum, f) => sum + f.maxOccupancy, 0);
    
    dummyData.push({
      time: minute,
      time2,
      total,
      perc: Math.round((total / maxOccupancy) * 100),
      traffic
    });
  }

  return dummyData;
};

// Set this to true to use dummy data, false for real data
const USE_DUMMY_DATA = false;
// =============================================================================

// Function to convert game start time to annotation timestamp
const convertGameStartTimeToAnnotation = (gameDate, gameStartTime, baseDate) => {
  if (!gameDate || !gameStartTime || !baseDate) return null;
  
  const [hours, minutes] = gameStartTime.split(':').map(Number);
  
  const chartBaseDate = new Date(baseDate);
  chartBaseDate.setHours(0, 0, 0, 0);
  
  const gameStartDate = new Date(chartBaseDate);
  gameStartDate.setHours(hours, minutes, 0, 0);
  
  const minutesFromBase = (gameStartDate.getTime() - chartBaseDate.getTime()) / 60000;
  const annotationTime = new Date(chartBaseDate.getTime() + minutesFromBase * 60000);
  
  return annotationTime.toISOString();
};

// Floor colors - distinct and accessible
const FLOOR_COLORS = {
  1: { normal: '#3b82f6', breach: '#d97706' },  // Blue / Amber
  2: { normal: '#e88559', breach: '#922b21' },  // Orange / Dark Red
  3: { normal: '#9792ec', breach: '#6c3483' },  // Lilac / Purple
  4: { normal: '#76ba92', breach: '#1e8449' },  // Green / Dark Green
};

const FloorOccupancyChart = ({ data, currentLocation, dayType, currentGame }) => {
  const authCtx = useContext(AuthContext);
  const [dataValues, setDataValues] = useState([]);
  const [chartResolution, setChartResolution] = useState(1);
  const [loading, setLoading] = useState(true);
  const resolutions = [1, 5, 10, 15, 30, 60];
  const scrollContainerRef = useRef(null);

  // Get floors config - only render this chart for multi-floor locations
  const floors = useMemo(() => currentLocation?.floors || [], [currentLocation?.floors]);
  const isMultiFloor = floors.length > 1;
  const totalMaxOccupancy = currentLocation?.maxOccupancy || floors.reduce((sum, f) => sum + f.maxOccupancy, 0);

  // Pre-compute floor zone ID mappings from currentLocation.zones (matches RetailMap approach)
  const floorZoneIdMap = useMemo(() => {
    const map = {};
    floors.forEach(floor => {
      map[floor.id] = currentLocation.zones
        ?.filter(z => z.floor === floor.id)
        .map(z => z.id) || [];
    });
    return map;
  }, [floors, currentLocation.zones]);

  // Use dummy data if enabled, otherwise use real data
  const chartData_ = useMemo(() => {
    if (USE_DUMMY_DATA && isMultiFloor) {
      return generateDummyFloorData(floors);
    }
    return data;
  }, [data, floors, isMultiFloor]);


  // Process and aggregate data by resolution
  const updateDataMap = useCallback((resolution = chartResolution) => {
    if (!chartData_ || chartData_.length === 0) {
      setLoading(false);
      return;
    }

    const dataMap = new Map();

    chartData_.forEach((item) => {
      if (item.time > 540) {
        // Inline getIntervalKey
        const baseDate = new Date(chartData_[0]?.time2 || new Date());
        baseDate.setHours(0, 0, 0, 0);
        const newDate = new Date(baseDate.getTime() + item.time * 60000);
        newDate.setMinutes(Math.floor(newDate.getMinutes() / resolution) * resolution, 0, 0);
        const key = newDate.toISOString();

        const entry = dataMap.get(key) || { 
          totalPerc: 0, 
          count: 0,
          floorData: floors.reduce((acc, floor) => {
            acc[floor.id] = { total: 0, count: 0 };
            return acc;
          }, {})
        };

        entry.totalPerc += item.perc;
        entry.count++;

        // Calculate floor occupancy using zone-to-floor mapping from currentLocation.zones
        floors.forEach(floor => {
          const floorZoneIds = floorZoneIdMap[floor.id] || [];
          const floorOccupancy = item.traffic
            ? item.traffic
                .filter(t => floorZoneIds.includes(t.id))
                .reduce((sum, t) => sum + (t.traffic || 0), 0)
            : 0;
          entry.floorData[floor.id].total += floorOccupancy;
          entry.floorData[floor.id].count++;
        });

        dataMap.set(key, { ...entry, dateTime: key });
      }
    });

    const aggregatedData = Array.from(dataMap.values()).map(({ dateTime, totalPerc, count, floorData }) => {
      const result = {
        dateTime,
        perc: Math.round(totalPerc / count),
        floors: {}
      };

      floors.forEach(floor => {
        const avgOccupancy = Math.round(floorData[floor.id].total / floorData[floor.id].count);
        const floorPerc = Math.round((avgOccupancy / floor.maxOccupancy) * 100);
        result.floors[floor.id] = {
          occupancy: avgOccupancy,
          perc: floorPerc,
          maxOccupancy: floor.maxOccupancy,
          breached: floorPerc > 100
        };
      });

      // Check if overall is breached
      result.overallBreached = result.perc > 100;

      return result;
    });

    setDataValues(aggregatedData);
    setLoading(false);
  }, [chartData_, chartResolution, floors, floorZoneIdMap]);

  useEffect(() => {
    setLoading(true);
    updateDataMap();
  }, [updateDataMap]);

  // Scroll to end on load
  useEffect(() => {
    const scrollElement = scrollContainerRef.current;
    if (scrollElement && !loading) {
      const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
      let start = null;

      const step = (timestamp) => {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        const d = Math.min(progress / 2000, 1);
        scrollElement.scrollLeft = maxScrollLeft * d;

        if (progress < 2000) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    }
  }, [loading, dataValues]);

  const maxValue = Math.max(...dataValues.map((item) => item.perc), 100);
  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt').trim();

  // Game start annotation
  const gameStartAnnotation = useMemo(() => {
    if (dayType !== 'game') return null;
    return currentGame?.date && currentGame?.gameStartTime && chartData_.length > 0 ? 
      convertGameStartTimeToAnnotation(currentGame.date, currentGame.gameStartTime, chartData_[0].time2) : null;
  }, [dayType, currentGame?.date, currentGame?.gameStartTime, chartData_]);

  // Build stacked datasets for each floor
  const chartDataConfig = useMemo(() => {
    const datasets = floors.map((floor, index) => {
      const floorColor = FLOOR_COLORS[floor.id] || FLOOR_COLORS[1];
      
      return {
        label: `${floor.label} (Max: ${floor.maxOccupancy})`,
        data: dataValues.map(item => {
          const floorData = item.floors[floor.id];
          // Convert floor occupancy to percentage of TOTAL store capacity for stacking
          return Math.round((floorData.occupancy / totalMaxOccupancy) * 100);
        }),
        backgroundColor: dataValues.map(item => {
          const floorData = item.floors[floor.id];
          return floorData.breached ? floorColor.breach : floorColor.normal;
        }),
        borderColor: dataValues.map(item => {
          // Red border if overall store is breached
          return item.overallBreached ? '#d97706' : 'transparent';
        }),
        borderWidth: dataValues.map(item => item.overallBreached ? 2 : 0),
        borderRadius: index === floors.length - 1 ? 2.5 : 0, // Round only top segment
        barThickness: chartResolution,
        stack: 'floors',
      };
    });

    return {
      labels: dataValues.map((item) => item.dateTime),
      datasets
    };
  }, [dataValues, floors, totalMaxOccupancy, chartResolution]);

  // Chart options with 100% threshold line
  const mainChartOptions = useMemo(() => ({
    scales: {
      x: {
        type: "time",
        title: {
          display: true,
          text: "Time",
          color: primaryTextColor,
        },
        time: {
          unit: "hour",
          displayFormats: {
            hour: "h A",
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          source: "auto",
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
        stacked: true,
      },
      y: {
        display: true,
        position: 'left',
        title: {
          color: primaryTextColor,
          display: true,
          text: "Occupancy %",
        },
        grid: {
          display: false,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          beginAtZero: true,
          stepSize: 20,
          callback: function (value) {
            if (value % 20 === 0) {
              return value + '%';
            }
          },
        },
        max: Math.max(Math.ceil(maxValue / 20) * 20, 120), // At least 120% to show breaches
        stacked: true,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          title: (context) => {
            const date = new Date(context[0].label);
            return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
          },
          label: (context) => {
            const dataIndex = context.dataIndex;
            const item = dataValues[dataIndex];
            const floor = floors[context.datasetIndex];
            const floorData = item.floors[floor.id];
            
            const breachIndicator = floorData.breached ? ' OVER CAPACITY \u26A0' : '';
            return `${floor.label}: ${floorData.occupancy} people (${floorData.perc}% of ${floor.maxOccupancy})${breachIndicator}`;
          },
          afterBody: (context) => {
            const dataIndex = context[0].dataIndex;
            const item = dataValues[dataIndex];
            const totalOccupancy = floors.reduce((sum, f) => sum + item.floors[f.id].occupancy, 0);
            const overallIndicator = item.overallBreached ? ' STORE OVER CAPACITY \u26A0' : '';
            return [`──────────`, `Total: ${totalOccupancy} people (${item.perc}% of ${totalMaxOccupancy})${overallIndicator}`];
          }
        },
        mode: 'index',
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'rectRounded',
          padding: 20,
          boxWidth: 15,
          generateLabels: function(chart) {
            const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            
            // Add capacity threshold indicator
            const capacityLabel = {
              text: '100% Capacity',
              fillStyle: 'transparent',
              strokeStyle: '#d97706',
              lineWidth: 2,
              lineDash: [6, 3],
              pointStyle: 'line',
              hidden: false,
              index: -3
            };

            // Add over capacity indicator
            const overCapacityLabel = {
              text: 'Over Capacity \u26A0',
              fillStyle: '#c0392b',
              strokeStyle: '#c0392b',
              lineWidth: 0,
              pointStyle: 'rectRounded',
              hidden: false,
              index: -2
            };
            
            // Add game start indicator if we have one
            if (gameStartAnnotation) {
              const gameStartLabel = {
                text: "Game Start",//authCtx.league === "mlb" ? "First Pitch" : authCtx.league === "nfl" ? "Kick Off" : "Game Start",
                fillStyle: 'transparent',
                strokeStyle: CHART_THEME.gameStart,
                lineWidth: 3,
                lineDash: [8, 2],
                pointStyle: 'line',
                hidden: false,
                index: -1
              };
              
              return [capacityLabel, overCapacityLabel, gameStartLabel, ...defaultLabels];
            }
            
            return [capacityLabel, overCapacityLabel, ...defaultLabels];
          }
        },
        onClick: function(evt, legendItem, legend) {
          // Don't toggle capacity line, over capacity indicator, or game start
          if (legendItem.index < 0) return;
          ChartJS.defaults.plugins.legend.onClick.call(this, evt, legendItem, legend);
        }
      },
      annotation: {
        annotations: {
          // 100% capacity threshold line
          capacityLine: {
            type: 'line',
            yMin: 100,
            yMax: 100,
            borderColor: '#d97706',
            borderWidth: 2,
            borderDash: [6, 3],
            label: {
              display: false
            }
          },
          // Game start annotation
          ...(gameStartAnnotation ? {
            gameStart: {
              type: 'line',
              mode: 'vertical',
              scaleID: 'x',
              value: gameStartAnnotation,
              borderColor: CHART_THEME.gameStart,
              borderWidth: 2,
              borderDash: [8, 4]
            }
          } : {})
        }
      },
    },
  }), [gameStartAnnotation, primaryTextColor, maxValue, dataValues, floors, totalMaxOccupancy, authCtx.league]);

  // Don't render for single-floor locations
  if (!isMultiFloor) {
    return null;
  }

  if (loading) {
    return (
      <div 
        className="panel-2"
        style={{
          padding: "24px",
          borderRadius: "36px",
          marginBottom: "16px",
          textAlign: "center",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        }}>
        Loading floor occupancy data...
      </div>
    );
  }

  return (
    <div className="graph panel-2 cx-chart-card">
      <ChartHeader
        title="Floor occupancy"
        subtitle="Occupancy by floor, stacked. The dashed line is the store's maximum."
        controls={<ResolutionToggle resolutions={resolutions} value={chartResolution} onChange={setChartResolution} />}
      />

      {/* Chart */}
      <div style={{ 
        position: 'relative',
        height: '350px',
        marginTop: '8px',
      }}>
        <div
          ref={scrollContainerRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowX: 'auto',
          }}
        >
          <div style={{
            width: '100%',
            height: '100%'
          }}>
            <Bar 
              key={`floor-occupancy-${dayType}`}
              data={chartDataConfig} 
              options={applyChartTheme(mainChartOptions)} 
            />
          </div>
        </div>
      </div>

    </div>
  );
};

export default FloorOccupancyChart;